import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';

import type { LearningOpportunityState } from '../data-contract';
import {
    type EditableLearningOpportunityState,
    editableLearningOpportunityStates,
    getLearningLanguageLabel,
    stateTranslationKey,
} from '../helpers';
import { CourseStateIndicator } from './CourseStateIndicator';
import { DateText } from './DateText';

export const CourseMetadataRow: FunctionComponent<{
    readonly isEditing?: boolean;
    readonly learningLanguage?: string;
    readonly ownerName?: string;
    readonly provider?: string;
    readonly state: LearningOpportunityState;
    readonly updatedAt?: string;
    readonly onStateChange?: (next: EditableLearningOpportunityState) => void;
}> = ({ isEditing = false, learningLanguage, ownerName, onStateChange, provider, state, updatedAt }) => {
    const translate = useTranslate();
    const learningLanguageLabel = getLearningLanguageLabel(learningLanguage, translate);
    const handleStateSelect = (next: EditableLearningOpportunityState): void => {
        if (next === state) return;
        onStateChange?.(next);
    };

    return (
        <div className="flex flex-wrap items-center gap-x-xxl gap-y-s text-body-s font-medium text-text-content-subtle">
            {isEditing ? (
                <DropdownMenu>
                    <DropdownMenuTrigger>
                        <Button
                            aria-label={translate('pages.learning-opportunities.headlines.state')}
                            className="!bg-transparent !p-0 !shadow-none hover:!bg-transparent hover:!opacity-100"
                            type="button"
                            variant="tertiary"
                        >
                            <CourseStateIndicator state={state} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        {editableLearningOpportunityStates.map((stateOption) => (
                            <DropdownMenuItem
                                key={stateOption}
                                disabled={stateOption === state}
                                onSelect={() => handleStateSelect(stateOption)}
                            >
                                {translate(stateTranslationKey(stateOption))}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <CourseStateIndicator state={state} />
            )}
            {is.nonEmptyString(provider) && (
                <span>
                    {translate('pages.learning-opportunities.provider')}:{' '}
                    <span className="font-normal">{translate(`providers.${provider}` as TranslationKey)}</span>
                </span>
            )}
            {is.nonEmptyString(ownerName) && (
                <span>
                    {translate('pages.learning-opportunities.owner-name')}:{' '}
                    <span className="font-normal">{ownerName}</span>
                </span>
            )}
            {is.nonEmptyString(learningLanguage) && (
                <span>
                    {translate('pages.learning-opportunities.learning-language')}:{' '}
                    <span className="font-normal">{learningLanguageLabel}</span>
                </span>
            )}
            {is.nonEmptyString(updatedAt) && (
                <span>
                    {translate('pages.learning-opportunities.headlines.updated-at')}:{' '}
                    <span className="font-normal">
                        <DateText value={updatedAt} />
                    </span>
                </span>
            )}
        </div>
    );
};
